/**
 * Service-surface building blocks. All compose @dreamtree-org/twreact-ui;
 * none is a new styled primitive.
 */
import { useState } from 'react';
import { Alert, Badge, Button, Table } from '@dreamtree-org/twreact-ui';
import { ShieldAlert } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';
import { STAGE_LABEL, STAGE_VARIANT, TICKET_STATUS, PRIORITY, SLA_LABEL, ESTIMATE_STATUS, CLAIM_STATUS, STORAGE_ACCESS } from '../domain';

/** Badge from a { key: { label, variant } } map. */
export function MapBadge({ map, value, size = 'sm' }) {
  const m = map[value] || { label: String(value || '—').replace(/_/g, ' '), variant: 'default' };
  return (
    <Badge variant={m.variant} size={size}>
      {m.label}
    </Badge>
  );
}

export const StageBadge = ({ stage, size }) => (
  <Badge variant={STAGE_VARIANT[stage] || 'default'} size={size || 'sm'}>
    {STAGE_LABEL[stage] || stage}
  </Badge>
);
export const TicketStatusBadge = ({ status }) => <MapBadge map={TICKET_STATUS} value={status} />;
export const PriorityBadge = ({ priority }) => <MapBadge map={PRIORITY} value={priority} />;
export const SlaBadge = ({ sla }) => <MapBadge map={SLA_LABEL} value={sla} />;
export const EstimateBadge = ({ status }) => <MapBadge map={ESTIMATE_STATUS} value={status} />;
export const ClaimBadge = ({ status }) => <MapBadge map={CLAIM_STATUS} value={status} />;

/** twreact Table with the defaults this surface wants (no CRUD actions, no toolbar). */
export function DataTable(props) {
  return <Table withAction={false} showSerial={false} showToolbar={false} sortable={false} {...props} />;
}

/** Data-handling restrictions from the customer's consent (R-04, NFR-04). */
export function RestrictionsBanner({ consent }) {
  if (!consent) return null;
  const access = STORAGE_ACCESS[consent.storageAccess] || STORAGE_ACCESS.none;
  return (
    <Alert variant="warning" title={`Data handling: ${access.label}`}>
      <ul className="list-disc pl-5 space-y-1 text-sm">
        <li>{access.detail}</li>
        <li>{consent.dataWipeAllowed ? 'Storage wipe allowed by the customer.' : 'Do not wipe or reformat storage.'}</li>
        <li>{consent.credentialsPolicy}</li>
      </ul>
      <div className="mt-1 text-xs opacity-70 flex items-center gap-1">
        <ShieldAlert size={14} aria-hidden /> Consent {consent.version}
      </div>
    </Alert>
  );
}

/**
 * Run @api registry mutations with one shared busy/error/notice state.
 *   const act = useAction();  await act.run('serviceJobMove', { id, to }, 'Moved');
 * Resolves to the response body, or null on failure (error shown via <ActionResult>).
 */
export function useAction() {
  const { fetchApi } = useAppApi();
  const [state, setState] = useState({ busy: '', error: '', notice: '' });
  const run = async (apiKey, params, notice = '', busyKey = apiKey) => {
    setState({ busy: busyKey, error: '', notice: '' });
    try {
      const res = await fetchApi(apiKey, params, false);
      setState({ busy: '', error: '', notice });
      return res;
    } catch (ex) {
      setState({ busy: '', error: errorText(ex), notice: '' });
      return null;
    }
  };
  const clear = () => setState({ busy: '', error: '', notice: '' });
  return { ...state, run, clear };
}

/** Inline result of the last action (error or success). */
export function ActionResult({ action }) {
  if (action.error) {
    return (
      <Alert variant="error" title="Not done" dismissible onDismiss={action.clear}>
        {action.error}
      </Alert>
    );
  }
  if (action.notice) {
    return (
      <Alert variant="success" dismissible onDismiss={action.clear}>
        {action.notice}
      </Alert>
    );
  }
  return null;
}

export function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function formatTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

/** How a handover code works, shown under every code field. */
export const OTP_HINT = 'Text the customer a code, then type the 6 digits they read out. Codes expire after 10 minutes.';

/**
 * "Text the customer a code" for a handover: purpose custody (id = ticket) or
 * return (id = job). Shows where it went; in local/demo builds the server also
 * echoes the code (OTP_DEV_ECHO) so the flow can be tried without a phone.
 */
export function HandoverCode({ purpose, id, disabled }) {
  const act = useAction();
  const [sent, setSent] = useState(null);
  const send = async () => {
    const res = await act.run('serviceHandoverCode', { purpose, id }, '', 'code');
    if (res) setSent(res.data);
  };
  return (
    <div className="space-y-1">
      <Button size="sm" variant="outline" onClick={send} loading={act.busy === 'code'} disabled={disabled}>
        {sent ? 'Send a new code' : 'Text the customer a code'}
      </Button>
      {sent ? (
        <p className="text-xs opacity-80">
          Sent to {sent.sent_to}.{sent.dev_code ? <> Demo build: the code is <strong className="font-mono">{sent.dev_code}</strong>.</> : null}
        </p>
      ) : null}
      {act.error ? <p className="text-xs text-error-600">{act.error}</p> : null}
    </div>
  );
}

/** A photo tile: the image when it has a (signed) url, else a labelled placeholder. */
export function PhotoTile({ label, url }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block aspect-[4/3] overflow-hidden rounded-md border border-[var(--app-border)]">
        <img src={url} alt={label} className="h-full w-full object-cover" loading="lazy" />
      </a>
    );
  }
  return (
    <div className="aspect-[4/3] rounded-md border border-dashed border-[var(--app-border)] bg-[var(--app-bg)] flex items-center justify-center p-2 text-center text-xs opacity-80">
      {label}
    </div>
  );
}
